import { Component, OnInit, OnDestroy, ChangeDetectorRef, ChangeDetectionStrategy, ViewChild } from '@angular/core';
import { Observable } from 'rxjs';
import { Store } from '@ngrx/store';
import { Actions } from '@ngrx/effects';
import { Router, ActivatedRoute } from '@angular/router';
import { MatSort, MatPaginator, Sort, MatTableDataSource, MatDialog } from '@angular/material';
import { Subject } from 'rxjs/Subject';
import { ModuleConfiguration } from '../extracts.module.config';
import * as fromStore from '../store/reducers';
import * as dashboardActions from '../../pillar/dashboard/store/actions';
import * as extractsActions from '../store/actions';
import { CurrentUserService } from '../../security/services/CurrentUserService';
import { BaseAddressInterceptor } from '../../core/interceptors/BaseAddressInterceptor';
import { AlertsComponent } from '../../pillar/alerts/components/alerts.component';
import { SearchComponent } from '../../pillar/search/components/search.component';
import { ExtractFilterDialogComponent } from './extract-filter-dialog.component';
import { ExtractModel } from '../models/ExtractModel';
import { ExtractListModel } from '../models/ExtractListModel';
import { CalOmsExtractWorkspaceModel } from '../models/CalOmsExtractWorkspaceModel';
import { CalOmsExtractWorkspaceFilterModel } from '../models/CalOmsExtractWorkspaceFilterModel';
import { StoreDataSource } from '../../core/services/StoreDataSource';
import { ACTION_ERROR, ActionError } from '../../pillar/common/actions/ActionError';

@Component({
  selector: 'brd-caloms-extract-workspace',
  templateUrl: './caloms-extract-workspace.component.html',
  styleUrls: ['./caloms-extract-workspace.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CalOmsExtractWorkspaceComponent implements OnInit, OnDestroy {
  activityDisplayedColumns = ['fSN', 'transactionDateTime', 'providerId', 'providersParticipantIdentifier', 'clientFirstName', 'typeOfForm', 'admissionDate', 'annualUpdateDischargeDate', 'submittedToCalOMS', 'lastUploadToStateDate', 'reasonForNotSubmitting'];
  noActivityDisplayedColumns = ['extractGenerateDate', 'providerId', 'reportMonth'];
  displayedColumns = [];
  extractWorkspaceListLoading$: Observable<boolean>;
  extractWorkspaceList$: Observable<CalOmsExtractWorkspaceModel[]>;
  extractCategorySelected$: Observable<ExtractModel>;
  extractListSelected$: Observable<ExtractListModel>;
  destroyed$: Subject<boolean> = new Subject<boolean>();
  extractSelected: ExtractListModel;
  dataSource: StoreDataSource<CalOmsExtractWorkspaceModel>;
  extractCategorySelected: ExtractModel;
  extractWorkspaceList: CalOmsExtractWorkspaceModel[];
  searchInput = 'ExtractsModule_SearchInput';
  selectedExtractList_ExtractListModel = 'ExtractsModule_SelectedExtractListType';
  selectedExtractCategory_ExtractModel = 'ExtractsModule_SelectedExtractModel';
  editExtractsPermission = 'ExtractsModule.Extracts:Edit';
  selectedAll: boolean = true;
  filterModel: CalOmsExtractWorkspaceFilterModel;

  @ViewChild(MatSort)
  sort: MatSort;
  @ViewChild(SearchComponent)
  search: SearchComponent;
  @ViewChild(AlertsComponent)
  alerts: AlertsComponent;

  constructor(private store: Store<any>,
    private router: Router,
    private route: ActivatedRoute,
    private currentUserService: CurrentUserService,
    private baseAddressInterceptor: BaseAddressInterceptor,
    private dialog: MatDialog,
    private changeDetectorRef: ChangeDetectorRef,
    private actions$: Actions) {
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
  }

  ngOnInit() {
    this.checkSelected();
    this.dataSource = new StoreDataSource<CalOmsExtractWorkspaceModel>(
      this.destroyed$,
      null,
      this.store,
      this.store.select(fromStore.getExtractWorkspaceList),
      this.store.select(fromStore.getExtractWorkspaceListTotalCount),
      this.store.select(fromStore.getExtractWorkspaceListLoading),
      (p, s, search) => new extractsActions.GetSelectedExtractAction(ModuleConfiguration.moduleName,
        CalOmsExtractWorkspaceModel,
        {
          key: this.extractSelected.key,
          page: null,
          pageSize: null,
          sort: s == null ? null : s.active,
          sortDirection: s == null ? null : s.direction,
          searchTerm: search.searchControl.value,
          extractType: this.extractSelected.fileType,
          model: this.extractCategorySelected
        }),
      this.sort,
      this.search
    );

    this.extractWorkspaceListLoading$ = this.store.select(fromStore.getExtractWorkspaceListLoading);
    this.extractWorkspaceList$ = this.store.select(fromStore.getExtractWorkspaceList);
    this.extractCategorySelected$ = this.store.select(fromStore.getExtractCategorySelected);
    this.extractListSelected$ = this.store.select(fromStore.getExtractListSelection);

    this.store.select(fromStore.getExtractCategorySelected)
      .takeUntil(this.destroyed$)
      .subscribe((v: ExtractModel) => {
        if (v) {
          this.extractCategorySelected = v;
          this.changeDetectorRef.markForCheck();
        }
      });

    this.store.select(fromStore.getExtractListSelection)
      .takeUntil(this.destroyed$)
      .subscribe((v: ExtractListModel) => {
        if (v) {
          this.extractSelected = v;
          this.getData();
        }
      });

    this.store.select(fromStore.getExtractWorkspaceList)
      .takeUntil(this.destroyed$)
      .subscribe((v: CalOmsExtractWorkspaceModel[]) => {
        if (v) {
          this.extractWorkspaceList = v;
          this.selectedAll = this.extractWorkspaceList != null && this.extractWorkspaceList.every(ew => ew.submittedToCalOMS);
        }
      });

    this.actions$.ofType(extractsActions.FINALIZE_EXTRACT_SUCCESS)
      .takeUntil(this.destroyed$)
      .subscribe((a: extractsActions.FinalizeExtractSuccessAction) => {
        this.extractSelected.status = "Finalized";
        sessionStorage.setItem(this.selectedExtractList_ExtractListModel, JSON.stringify(this.extractSelected));
      });

    this.actions$.ofType(extractsActions.SAVE_EXTRACT_SUCCESS)
      .takeUntil(this.destroyed$)
      .subscribe((a: extractsActions.SaveExtractSuccessAction) => {
        this.getData();
      });

    this.search
      .searchControl
      .valueChanges
      .debounceTime(500)
      .takeUntil(this.destroyed$)
      .subscribe(() => {
        this.getData();
      });

    this.actions$.ofType(ACTION_ERROR)
      .takeUntil(this.destroyed$)
      .subscribe((a: ActionError) => {
        a.payload.errors.forEach(e => this.alerts.addError(e));
      });
  }

  back() {
    this.router.navigateByUrl( '/extracts' );
  }

  checkSelected() {
    var extractListString = sessionStorage.getItem(this.selectedExtractList_ExtractListModel);
    if (extractListString) {
      this.extractSelected = JSON.parse(extractListString);
    }
    var extractCategoryString = sessionStorage.getItem(this.selectedExtractCategory_ExtractModel);
    if (extractCategoryString) {
      this.extractCategorySelected = JSON.parse(extractCategoryString);
    }
    var searchInput = sessionStorage.getItem(this.searchInput);
    if (searchInput) {
      this.search.searchControl.setValue(searchInput);
    }
    switch (this.extractSelected.fileType) {
      case "Provider Activity":
      {
        this.displayedColumns = this.activityDisplayedColumns;
        break;
      }
      case "Provider No Activity":
      {
        this.displayedColumns = this.noActivityDisplayedColumns;
        break;
      }
      default:
      {
        break;
      }
    }
  }

  getData(filterModel: CalOmsExtractWorkspaceFilterModel = null) {
    this.store.dispatch(new extractsActions.GetSelectedExtractAction(
      ModuleConfiguration.moduleName,
      CalOmsExtractWorkspaceModel,
      {
        key: this.extractSelected.key,
        page: null,
        pageSize: null,
        sort: this.sort == null ? null : this.sort.active,
        sortDirection: this.sort == null ? null : this.sort.direction,
        searchTerm: this.search == null ? null : this.search.searchControl.value,
        extractType: this.extractSelected.fileType,
        model: this.extractCategorySelected,
        filterModel: filterModel
      }));
  }

  finalize(): void {
    if (this.extractWorkspaceList.every(i => this.canSaveExtract(i))) {
      this.store.dispatch(new extractsActions.FinalizeExtractAction({ extractKey: this.extractSelected.key, model: this.extractCategorySelected }));
    } else {
      this.alerts.addError ( "There was an error finalizing the changes. Reason For Not Submitting is required for unchecked Records." );
    }
  }

  toggle(item: CalOmsExtractWorkspaceModel, checked: boolean): void {
    item.submittedToCalOMS = checked;
    if (item.submittedToCalOMS) {
      item.reasonForNotSubmitting = "";
    }
    this.selectedAll = this.extractWorkspaceList != null && this.extractWorkspaceList.every(ew => ew.submittedToCalOMS);
  }

  onChange(event: Event, item: CalOmsExtractWorkspaceModel): void {
    item.reasonForNotSubmitting = (<HTMLInputElement>event.target).value;
  }

  filter(): void {
    var dialogRef = this.dialog.open(ExtractFilterDialogComponent,
      {
        panelClass: 'customized-dialog',
        width: '648px',
        data: {
          category: this.extractCategorySelected,
          extract: this.extractSelected,
          model: this.filterModel
  }
      });
    dialogRef.afterClosed()
      .subscribe((model: CalOmsExtractWorkspaceFilterModel) => {
        this.getData(model);
        this.filterModel = model;
      });
  }

  selectAll(checked: boolean): void {
    this.selectedAll = checked;
    if (this.extractWorkspaceList == null) {
      return;
    }
    this.extractWorkspaceList.forEach(t => t.submittedToCalOMS = checked);
  }

  selectedSome(): boolean {
    if (this.extractWorkspaceList == null) {
      return false;
    }
    return this.extractWorkspaceList.filter(ew => ew.submittedToCalOMS).length > 0 && !this.selectedAll;
  }

  save(): void {
    if (this.extractWorkspaceList.every(i => this.canSaveExtract(i))) {
      this.store.dispatch(new extractsActions.SaveExtractAction({ model: this.extractWorkspaceList }));
    } else {
      this.alerts.addError("There was an error saving the changes. Reason For Not Submitting is required for unchecked Records.");
    }
  }

  canSaveExtract( model: CalOmsExtractWorkspaceModel ): boolean {
    var isValid = true;
    if (!model.submittedToCalOMS && model.reasonForNotSubmitting === "") {
      isValid = false;
    }
    return isValid;
  }
}



// WEBPACK FOOTER //
// ./src/app/extracts/components/caloms-extract-workspace.component.ts